import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Row, Col, Spin, Select, Card, Modal, Table, Typography, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Redirect } from 'react-router-dom'
import moment from 'moment'
import { useQuery } from '@apollo/client'
import util from '../../util/util'
import '../../css/compliance.scss'
import {
  getPatientCompliance,
  getStats,
  getPatientList,
  getPatientComplianceExercise,
  getPatientComplianceMeasurement,
  getPatientComplianceFeedback,
} from '../../store/action/patient.action'
import { setPatient } from '../../store/action/patient.action'
import CompliancePie from './compliancepi'
import _ from 'lodash'
import { useHistory } from 'react-router'
import { constants } from '../../constants'
import { messageGroupBy } from '../../store/action/actioncases.action'
import { getHelp, pages } from '../../tutorials/index'
import { updateUserVisitedPages } from '../../store/action/user.action'
import { getDashboardData } from '../../services/dashboard.service'
import { getEventSource, getEventName } from '../../util/activityLog.util'

const { Title, Text } = Typography

const Compliance = (props) => {
  const history = useHistory()
  const [data, setData] = useState({
    startdate: moment()
      .subtract(2, 'week')
      .startOf('week')
      .format('MM/DD/YYYY'),
    enddate: moment().subtract(1, 'weeks').endOf('week').format('MM/DD/YYYY'),
    type: 'All', // Default to 'All', will be updated when stats load
  })
  const [value, setValue] = useState('2w')
  const [spin, setSpin] = useState(false)
  const [complianceSpin, setComplianceSpin] = useState({
    exercise: false,
    measurement: false,
    feedback: false,
    overall: false,
  })
  const [summary, setSummary] = useState({
    exerciseSummary: {
      incomplete: { count: 0, patientids: [] },
      completed: { count: 0, patientids: [] },
      partially: { count: 0, patientids: [] },
    },
    measurementSummary: {
      incomplete: { count: 0, patientids: [] },
      completed: { count: 0, patientids: [] },
      partially: { count: 0, patientids: [] },
    },
    feedBackSummary: {
      incomplete: { count: 0, patientids: [] },
      completed: { count: 0, patientids: [] },
      partially: { count: 0, patientids: [] },
    },
    overAllSummary: {
      incomplete: { count: 0, patientids: [] },
      completed: { count: 0, patientids: [] },
      partially: { count: 0, patientids: [] },
    },
  })

  // Dashboard metrics state
  const [dashboardDateRange, setDashboardDateRange] = useState('7d')
  const [modalVisible, setModalVisible] = useState(false)
  const [modalData, setModalData] = useState([])
  const [modalTitle, setModalTitle] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // Dashboard date range options
  const dashboardDateRangeOptions = [
    { value: '1d', label: '1 day' },
    { value: '3d', label: '3 days' },
    { value: '7d', label: '7 days' },
    { value: '15d', label: '15 days' },
    { value: '1m', label: '1 month' },
    { value: '2m', label: '2 months' },
    { value: '3m', label: '3 months' },
  ]

  // Get dashboard data for patient metrics
  const { data: dashboardData, loading: dashboardLoading, refetch: refetchDashboard } = useQuery(
    getDashboardData,
    {
      variables: {
        input: {
          customerid: props.customer?._id,
          fromDate: dashboardDateRange,
        },
      },
      skip: !props.customer?._id,
      fetchPolicy: 'cache-and-network',
    }
  )

  const dashboard = dashboardData?.getDashBoardData || {}

  // Get readable date range label for tooltips
  const getDateRangeLabel = () => {
    const labels = {
      '1d': '1 day',
      '3d': '3 days',
      '7d': '7 days',
      '15d': '15 days',
      '1m': '1 month',
      '2m': '2 months',
      '3m': '3 months',
    }
    return labels[dashboardDateRange] || dashboardDateRange
  }

  // Handle patient card click
  const handleCardClick = (type, title) => {
    let data = []
    switch (type) {
      case 'active':
        data = dashboard.activePatients || []
        break
      case 'activity':
        data = dashboard.activityPatients || []
        break
      case 'new':
        data = dashboard.newPatients || []
        break
      case 'registered':
        data = dashboard.registeredPatients || []
        break
    }
    setModalData(data)
    setModalTitle(title)
    setModalVisible(true)
    setCurrentPage(1)
  }

  // Handle row click to navigate to patient detail page
  const handleRowClick = async (record) => {
    // Clear any existing patient state
    await props.setPatient({})
    // Navigate to the patient detail page
    history.push({ pathname: '/patient/' + record.patientid, state: record })
  }

  // Table columns for patient modal
  const patientColumns = [
    {
      title: 'Patient Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{record.firstname} {record.lastname}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.patientid}</Text>
        </div>
      ),
    },
    {
      title: 'Action Type',
      dataIndex: 'actiontype',
      key: 'actiontype',
      // Friendly source (Patient / Clinic / MoveAI) instead of the raw internal
      // 'patietaction' token. Roster rows with no real activity → N/A.
      render: (_, record) => {
        if (!record.actiontype && !record.method) return 'N/A'
        const source = getEventSource(record)
        return source === 'Patients' ? 'Patient' : source
      },
    },
    {
      title: 'Action',
      dataIndex: 'method',
      key: 'method',
      // Human-readable event name (Exercise, Questionnaire, Login, Case
      // Management, ...) instead of the raw server method name.
      render: (_, record) => {
        if (!record.actiontype && !record.method) return 'N/A'
        return getEventName(record) || 'N/A'
      },
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivityDate',
      key: 'lastActivityDate',
      render: (date, record) => {
        const d = date || record.createdAt
        return d ? new Date(d).toLocaleDateString() : 'N/A'
      },
    },
  ]

  // Patient metrics card data
  const patientCardData = [
    {
      title: 'Total Active Patients',
      count: dashboard.activePatients?.length || 0,
      color: '#000000',
      type: 'active',
      tooltip: 'Patients with active schedules (exercise, device, or questionnaire)',
    },
    {
      title: 'Total Patients with Activity',
      count: dashboard.activityPatients?.length || 0,
      color: '#000000',
      type: 'activity',
      tooltip: `Patients who have performed some activity in the last ${getDateRangeLabel()}`,
    },
    {
      title: 'Total New Patients',
      type: 'combined',
      leftCount: dashboard.registeredPatients?.length || 0,
      leftLabel: 'Registered',
      leftType: 'registered',
      rightCount: dashboard.newPatients?.length || 0,
      rightLabel: 'Logged In',
      rightType: 'new',
      color: '#52c41a',
      tooltip: `Registered: New patients created in the last ${getDateRangeLabel()}\nLogged In: Patients who logged in for the first time in the last ${getDateRangeLabel()}`,
    },
  ]

  useEffect(() => {
    // setSpin(true);

    ;(async () => {
      props.getStats(props.customer?._id)
      getComplianceSummary(data)
    })()
    props.messageGroupBy(null)
    getHelp(pages.COMPLIANCEPAGE)
  }, [])

  // Update default surgery type when stats are loaded or customer changes
  useEffect(() => {
    if (props.stats?.surgeryTypes && props.stats.surgeryTypes.length > 0) {
      // Check if the currently selected surgery type exists in the new stats
      const currentTypeExists = props.stats.surgeryTypes.some(
        st => st.name.toLowerCase() === data.type.toLowerCase()
      ) || data.type === 'All'

      if (!currentTypeExists) {
        // Current surgery type doesn't exist for this customer, reset to default
        const defaultType = props.stats.surgeryTypes.length === 1
          ? props.stats.surgeryTypes[0].name
          : 'All'
        setData(prev => ({ ...prev, type: defaultType }))
      } else if (props.stats.surgeryTypes.length === 1 && data.type === 'All') {
        // If there's only one surgery type and we're on 'All', switch to that type
        const defaultType = props.stats.surgeryTypes[0].name
        setData(prev => ({ ...prev, type: defaultType }))
      }
    }
  }, [props.stats])

  const fetchPatientList = (patientids, name, type) => {
    let patientIds = patientids
    if (!_.isEmpty(patientIds)) patientIds = _.uniqBy(patientIds)
    console.log(patientIds, 'patientIds')
    props.fetchPatient(
      {
        customerid: props.customer?._id,
        metadata: { current: 1, pageSize: 20 },
        filters: {
          ...props.filters,
          patients: patientIds,
          surgeon:
            props.decoded?.role == constants.ROLE.SURGEON
              ? [props.user?.surgeon]
              : props.filters?.surgeon,
        },
        sorter: { order: 'ascend', field: 'surgery' },
      },
      props.selectedPatients
    )

    history.push({
      pathname: '/patient',
      state: {
        patientids: patientIds,
        name: `Patients ${type}  the ${name} schedule`,
      },
    })
  }

  const getComplianceSummary = async (summ) => {
    const customerid = props.customer?._id
    setComplianceSpin({
      exercise: true,
      measurement: true,
      feedback: true,
      overall: true,
    })

    try {
      // const exercisePromise = props.getPatientComplianceExercise(summ, customerid)
      //   .then((exercise) => {
      //     setSummary((prev) => ({ ...prev, exerciseSummary: exercise.exerciseSummary }));
      //     setComplianceSpin((prev) => ({ ...prev, exercise: false }));
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching exercise data:", error);
      //     setComplianceSpin((prev) => ({ ...prev, exercise: false }));
      //   });

      // const measurementPromise = props.getPatientComplianceMeasurement(summ, customerid)
      //   .then((measurement) => {
      //     setSummary((prev) => ({ ...prev, measurementSummary: measurement.measurementSummary }));
      //     setComplianceSpin((prev) => ({ ...prev, measurement: false }));
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching measurement data:", error);
      //     setComplianceSpin((prev) => ({ ...prev, measurement: false }));
      //   });

      // const feedbackPromise = props.getPatientComplianceFeedback(summ, customerid)
      //   .then((feedback) => {
      //     setSummary((prev) => ({ ...prev, feedBackSummary: feedback.feedBackSummary }));
      //     setComplianceSpin((prev) => ({ ...prev, feedback: false }));
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching feedback data:", error);
      //     setComplianceSpin((prev) => ({ ...prev, feedback: false }));
      //   });

      const overallPromise = props
        .getPatientCompliance(summ, customerid)
        .then((overall) => {
          setSummary((prev) => ({
            ...prev,
            overAllSummary: overall.overAllSummary,
            feedBackSummary: overall.feedBackSummary,
            measurementSummary: overall.measurementSummary,
            exerciseSummary: overall.exerciseSummary,
          }))
          setComplianceSpin((prev) => ({
            ...prev,
            overall: false,
            exercise: false,
            feedback: false,
            measurement: false,
          }))
        })
        .catch((error) => {
          console.error('Error fetching overall data:', error)
          setComplianceSpin((prev) => ({ ...prev, overall: false }))
        })

      // Start all promises but do not block on them
      await Promise.all([overallPromise])
    } catch (error) {
      console.error('Unexpected error in fetching compliance summary:', error)
    }
  }

  const handleSurgeryTypeChange = (surgerytype) => {
    setData({ ...data, type: surgerytype })
    getComplianceSummary({ ...data, type: surgerytype })
  }

  const handleComplianceChange = async (value) => {
    let newStartDate = null
    let newEndDate = moment()
      .subtract(1, 'weeks')
      .endOf('week')
      .format('MM/DD/YYYY')
    let allEndDate = moment().startOf('week').format('MM/DD/YYYY')

    switch (value) {
      case '1w':
        newStartDate = moment()
          .subtract(1, 'week')
          .startOf('week')
          .format('MM/DD/YYYY')
        break
      case '2w':
        newStartDate = moment()
          .subtract(2, 'week')
          .startOf('week')
          .format('MM/DD/YYYY')
        break
      case '4w':
        newStartDate = moment()
          .subtract(4, 'week')
          .startOf('week')
          .format('MM/DD/YYYY')
        break

      default:
        newStartDate = null
        break
    }
    setData({ ...data, startdate: newStartDate, enddate: newEndDate })
    setValue(value)
    getComplianceSummary({
      ...data,
      startdate: newStartDate,
      enddate: newEndDate,
    })
  }

  const complianceRangePicker = (
    <Select
      showSearch
      filterOption={(input, option) =>
        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      className='rangePickerSelect'
      value={value}
      onChange={handleComplianceChange}
    >
      <Select.Option value='1w'> 1 week</Select.Option>
      <Select.Option value='2w'> 2 weeks</Select.Option>
      <Select.Option value='4w'> 4 weeks</Select.Option>
      <Select.Option value='All'>All Time</Select.Option>
    </Select>
  )

  const surgeryTypePicker = (
    <Select
      showSearch
      filterOption={(input, option) =>
        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      className='rangePickerSelect'
      value={data.type}
      onChange={handleSurgeryTypeChange}
    >
      {props.stats?.surgeryTypes && props.stats.surgeryTypes.map((surgType) => (
        <Select.Option key={surgType.name} value={surgType.name}>
          {surgType.name.toUpperCase()}
        </Select.Option>
      ))}
      <Select.Option value='All'>ALL</Select.Option>
    </Select>
  )

  return props.token == undefined ? (
    <Redirect to='/login' />
  ) : (
    <>
      <Spin spinning={spin}>
        <Row type='flex' className='complianceContainer'>
          <div style={{ width: '100%' }}>
            <Row className='compliance-heading'>
              <p>
                {' '}
                {`Active: ${_.isEmpty(props.stats) ? '0' : props.stats?.active} | `}{' '}
              </p>
              &nbsp;
              {props.stats?.surgeryTypes && props.stats.surgeryTypes.map((surgType, index) => (
                <React.Fragment key={surgType.name}>
                  <p>
                    {' '}
                    {`${surgType.name.toUpperCase()}: ${surgType.count || '0'}`}{index < props.stats.surgeryTypes.length - 1 ? ' | ' : ' '}
                  </p>
                  {index < props.stats.surgeryTypes.length - 1 && <>&nbsp;</>}
                </React.Fragment>
              ))}
            </Row>
            <Row
              id='compliancefilter'
              className='chartContainer'
              justify='space-between'
            >
              <Col style={{ display: 'flex' }}>
                <Row>
                  <h3 style={{ margin: 0 }}>
                    {' '}
                    NUMBER OF PATIENTS COMPLIANCE STATUS IN THE LAST{' '}
                  </h3>
                </Row>
                <Row> {complianceRangePicker}</Row>
              </Col>
              <Col style={{ display: 'flex' }}>
                <Col>
                  <h3 style={{ margin: 0 }}> SURGERY TYPE </h3>
                </Col>
                <Col> {surgeryTypePicker}</Col>
              </Col>
            </Row>
            <Row
              id='compliancecharts'
              style={{ display: 'flex', justifyContent: 'space-evenly' }}
            >
              <Col className='pigraph'>
                {' '}
                <CompliancePie
                  fetchPatientList={fetchPatientList}
                  name='Exercises'
                  index={1}
                  data={summary.exerciseSummary}
                  isSpin={complianceSpin.exercise}
                />{' '}
              </Col>
              <Col className='pigraph'>
                {' '}
                <CompliancePie
                  fetchPatientList={fetchPatientList}
                  name='Measurements'
                  index={2}
                  data={summary.measurementSummary}
                  isSpin={complianceSpin.measurement}
                />{' '}
              </Col>
              <Col className='pigraph'>
                {' '}
                <CompliancePie
                  fetchPatientList={fetchPatientList}
                  name='Questionnaires'
                  index={3}
                  data={summary.feedBackSummary}
                  isSpin={complianceSpin.feedback}
                />{' '}
              </Col>
              <Col id='complianceoverall' className='pigraph'>
                {' '}
                <CompliancePie
                  fetchPatientList={fetchPatientList}
                  name='Overall'
                  index={4}
                  data={summary.overAllSummary}
                  isSpin={complianceSpin.overall}
                />{' '}
              </Col>
            </Row>

            {/* Patient Activity Metrics Section */}
            <Row style={{ marginTop: '32px', padding: '0 16px' }}>
              <Col span={24}>
                <Row justify='space-between' align='middle' style={{ marginBottom: '16px' }}>
                  <Col>
                    <Title level={4} style={{ margin: 0, color: '#000000' }}>
                      Patient Activity
                    </Title>
                  </Col>
                  <Col>
                    <Select
                      style={{ width: 150 }}
                      value={dashboardDateRange}
                      onChange={(value) => {
                        setDashboardDateRange(value)
                        refetchDashboard()
                      }}
                    >
                      {dashboardDateRangeOptions.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
                <Spin spinning={dashboardLoading}>
                  <Row gutter={[24, 24]}>
                    {patientCardData.map((card) => (
                      <Col xs={24} sm={12} lg={8} key={card.type}>
                        <Card
                          hoverable={card.type !== 'combined'}
                          onClick={card.type !== 'combined' ? () => handleCardClick(card.type, card.title) : undefined}
                          style={{
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            cursor: card.type !== 'combined' ? 'pointer' : 'default',
                            height: '100%'
                          }}
                          bodyStyle={{ padding: '32px' }}
                        >
                          {card.type === 'combined' ? (
                            <div style={{ textAlign: 'center' }}>
                              <Text style={{ display: 'block', fontSize: '16px', color: 'rgba(0, 0, 0, 0.65)', marginBottom: '16px', fontWeight: 500 }}>
                                {card.title}
                                {card.tooltip && (
                                  <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{card.tooltip}</span>}>
                                    <QuestionCircleOutlined style={{ marginLeft: '8px', color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                              </Text>
                              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                <div
                                  style={{ textAlign: 'center', cursor: 'pointer', flex: 1 }}
                                  onClick={() => handleCardClick(card.leftType, `${card.title} - ${card.leftLabel}`)}
                                >
                                  <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                                    {card.leftCount}
                                  </Title>
                                  <Text style={{ display: 'block', fontSize: '14px', color: 'rgba(0, 0, 0, 0.45)', marginTop: '8px' }}>
                                    {card.leftLabel}
                                  </Text>
                                </div>
                                <div
                                  style={{ textAlign: 'center', cursor: 'pointer', flex: 1 }}
                                  onClick={() => handleCardClick(card.rightType, `${card.title} - ${card.rightLabel}`)}
                                >
                                  <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                                    {card.rightCount}
                                  </Title>
                                  <Text style={{ display: 'block', fontSize: '14px', color: 'rgba(0, 0, 0, 0.45)', marginTop: '8px' }}>
                                    {card.rightLabel}
                                  </Text>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center' }}>
                              <Text style={{ display: 'block', fontSize: '16px', color: 'rgba(0, 0, 0, 0.65)', marginBottom: '16px', fontWeight: 500 }}>
                                {card.title}
                                {card.tooltip && (
                                  <Tooltip title={card.tooltip}>
                                    <QuestionCircleOutlined style={{ marginLeft: '8px', color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                              </Text>
                              <Title level={1} style={{ margin: 0, color: card.color, fontWeight: 700, fontSize: '64px', lineHeight: 1 }}>
                                {card.count}
                              </Title>
                            </div>
                          )}
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Spin>
              </Col>
            </Row>
          </div>
        </Row>
      </Spin>

      {/* Patient Details Modal */}
      <Modal
        title={modalTitle}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Table
          dataSource={modalData}
          columns={patientColumns}
          rowKey="_id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: modalData.length,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} patients`,
          }}
        />
      </Modal>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    token: util.verifyToken(state.profile.token),
    isLoading: state.profile.isLoading,
    customer: state.profile.customer,
    stats: state.patient.stats,
    pagination: state.patient.metadata,
    patients: state.patient.patients,
    defaultFilter: state.profile.user.filter,
    filters: state.patient.filters,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getStats: (customerid) => dispatch(getStats(customerid)),
    getPatientCompliance: (data, id) =>
      dispatch(getPatientCompliance(data, id)),
    getPatientComplianceExercise: (data, id) =>
      dispatch(getPatientComplianceExercise(data, id)),
    getPatientComplianceMeasurement: (data, id) =>
      dispatch(getPatientComplianceMeasurement(data, id)),
    getPatientComplianceFeedback: (data, id) =>
      dispatch(getPatientComplianceFeedback(data, id)),
    fetchPatient: (params, patients) =>
      dispatch(getPatientList(params, patients)),
    messageGroupBy: (data) => dispatch(messageGroupBy(data)),
    updateUserVisitedPages: (data) => dispatch(updateUserVisitedPages(data)),
    setPatient: (patient) => dispatch(setPatient(patient)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Compliance)
